import {useEffect} from "react";
import {useRouteError} from "react-router-dom";
import {resolveActiveLocale, type Locale} from "i18n/localePreference";
import {reportCaughtError, type ClientErrorSurface} from "./clientErrorReport";

/**
 * The thin React adapter that lets a route error boundary report what it caught
 * (kitecorp/transferify-ui#2581).
 *
 * It holds no rules: every decision about what may be on the wire, how it is bounded and when a report is
 * a duplicate lives in `clientErrorReport.ts`, which imports no React. This decides only WHEN — in an
 * effect rather than during render, for two reasons that both matter here:
 *
 * - render runs twice under StrictMode in development, and an effect does not, so the report is not
 *   doubled where it is easiest to notice and hardest to explain;
 * - the SSG prerender renders but runs no effect, so a route that throws during prerender does not try to
 *   post from Node, where there is no api base and no browser.
 *
 * The dependency list is the REPORT's inputs, not the error object: `useRouteError` hands back the same
 * value for as long as the boundary is mounted, and the session gate in `reportClientError` is what makes a
 * second call for the same failure a no-op anyway.
 */
export function useReportedRouteError(surface: ClientErrorSurface, forcedLocale?: Locale): void {
	const error = useRouteError();
	const locale = resolveActiveLocale(forcedLocale);
	useEffect(() => {
		// Not awaited and nothing cleans it up: the report resolves its brand through a lazy chunk (see
		// `reportCaughtError`), and an unmounted boundary's report is still a report worth having.
		void reportCaughtError({
			surface,
			error,
			locale,
			pathname: window.location.pathname,
		});
	}, [surface, error, locale]);
}
